<template>
  <div id="param-optimizer">
    <div class="container-fluid pt-4 px-4">
      <v-alert type="warning" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
        Runs entirely against the offline backtest engine — never places a real order, never changes live
        settings. A completed run's top candidate is auto-saved as a <strong>testing</strong> version (visible
        on the Settings page's Settings History card) for you to review and compare, never applied automatically.
      </v-alert>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="px-5 py-4">
          <span class="text-h6 font-weight-medium">New Optimization Run</span>
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <v-row align="center">
            <v-col cols="12" sm="6" md="3">
              <v-text-field v-model="form.fromDate" type="date" label="From" variant="outlined" density="comfortable" hide-details />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field v-model="form.toDate" type="date" label="To" variant="outlined" density="comfortable" hide-details />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="form.exitStrategyMode"
                :items="[{ title: 'Single-stage', value: 'single_stage' }, { title: 'Multi-stage', value: 'multi_stage' }]"
                label="Exit strategy" variant="outlined" density="comfortable" hide-details
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="form.searchStrategy"
                :items="searchStrategyOptions"
                label="Search strategy" variant="outlined" density="comfortable" hide-details
              />
            </v-col>
          </v-row>
          <v-row align="center" class="mt-1">
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model.number="form.foldCount" type="number" label="Fold count (blank = auto)"
                variant="outlined" density="comfortable" hide-details clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="9" class="d-flex align-center">
              <v-btn color="primary" variant="flat" rounded="lg" :loading="starting" @click="startRun">
                Start Optimization
              </v-btn>
            </v-col>
          </v-row>
          <div class="text-caption text-medium-emphasis mt-2">
            Grid search evaluates every parameter combination — can be slow for a wide multi-stage grid.
            Coordinate search sweeps one parameter at a time against the walk-forward stability score
            (faster, not guaranteed optimal). Random search samples N combinations for a longer/overnight run.
          </div>
          <v-alert v-if="startMessage" :type="startError ? 'error' : 'success'" variant="tonal" density="comfortable" class="mt-3" rounded="lg">
            {{ startMessage }}
          </v-alert>
        </v-card-text>
      </v-card>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-3 px-5 py-4">
          <span class="text-h6 font-weight-medium">Run History</span>
          <v-btn variant="text" size="small" :loading="!runsLoaded" @click="loadRuns">
            <font-awesome-icon icon="fa-solid fa-rotate-right" class="mr-1" /> Refresh
          </v-btn>
        </v-card-title>
        <v-table density="compact" class="custom-data-table">
          <thead>
            <tr>
              <th>Run</th><th>Range</th><th>Exit strategy</th><th>Status</th><th>Combos</th>
              <th>Best stability</th><th>Best expectancy</th><th>Trades</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="run in runs" :key="run.param_optimizer_run_id">
              <td>#{{ run.param_optimizer_run_id }}</td>
              <td>{{ run.from_date }} – {{ run.to_date }}</td>
              <td>{{ run.exit_strategy_mode }}</td>
              <td><v-chip size="small" variant="tonal" :color="statusColor(run.status)">{{ run.status }}</v-chip></td>
              <td>{{ run.combos_evaluated ?? '—' }}</td>
              <td>{{ isFiniteNumber(run.best_candidate_stability_score) ? Number(run.best_candidate_stability_score).toFixed(2) : '—' }}</td>
              <td>{{ formatMoney(run.best_candidate_mean_expectancy_rs) }}</td>
              <td>{{ run.best_candidate_trade_count ?? '—' }}</td>
              <td>
                <v-btn size="small" variant="text" density="compact" @click="viewRun(run.param_optimizer_run_id)">View</v-btn>
              </td>
            </tr>
          </tbody>
          <template v-slot:no-data>
            <div class="py-6 text-center text-medium-emphasis">No optimization runs yet.</div>
          </template>
        </v-table>
      </v-card>

      <v-alert v-if="viewError" type="error" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
        {{ viewError }}
      </v-alert>

      <v-card v-if="viewingRun && !selectedRun" flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-text class="px-5 py-8 text-center text-medium-emphasis">
          <v-progress-circular indeterminate color="primary" size="28" class="mb-2" /><br />
          Loading run…
        </v-card-text>
      </v-card>

      <v-card v-if="selectedRun" flat rounded="lg" class="gap-backtest-card">
        <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-3 px-5 py-4">
          <span class="text-h6 font-weight-medium">Run #{{ selectedRun.param_optimizer_run_id }} — ranked candidates</span>
          <v-chip size="small" variant="tonal" :color="statusColor(selectedRun.status)">{{ selectedRun.status }}</v-chip>
        </v-card-title>
        <v-card-text class="px-5 pb-5">
          <v-alert v-if="selectedRun.status === 'failed'" type="error" variant="tonal" density="comfortable" rounded="lg">
            {{ selectedRun.error_message || 'Run failed.' }}
          </v-alert>
          <template v-else-if="selectedRun.status === 'completed' && selectedRun.ranked_candidates">
            <div class="text-subtitle-2 font-weight-medium mb-2">Eligible (passed the majority-top-N gate)</div>
            <v-table density="compact" class="mb-4 custom-data-table">
              <thead>
                <tr><th>#</th><th>Changed settings</th><th>Stability</th><th>Mean expectancy</th><th>Trades</th><th></th><th></th></tr>
              </thead>
              <tbody>
                <tr v-for="(c, i) in selectedRun.ranked_candidates.eligible" :key="i">
                  <td>{{ i + 1 }}</td>
                  <td class="text-caption">{{ comboSummary(c.combo) }}</td>
                  <td>{{ Number(c.stabilityScore).toFixed(2) }}</td>
                  <td>{{ formatMoney(c.meanExpectancyRs) }}</td>
                  <td>{{ c.totalTrades }}</td>
                  <td><v-chip size="x-small" variant="tonal" :color="confidenceColor(c.confidenceTier)">{{ c.confidenceTier }}</v-chip></td>
                  <td><v-btn size="small" variant="tonal" color="primary" :loading="promoting === i" @click="promote(i)">Save as testing version</v-btn></td>
                </tr>
                <tr v-if="!selectedRun.ranked_candidates.eligible.length">
                  <td colspan="7" class="text-medium-emphasis text-center py-3">No combo cleared the stability + majority-gate bar for this run.</td>
                </tr>
              </tbody>
            </v-table>

            <div class="text-subtitle-2 font-weight-medium mb-2">Inconsistent — good on one fold, not a majority</div>
            <v-table density="compact" class="mb-4 custom-data-table">
              <tbody>
                <tr v-for="(c, i) in selectedRun.ranked_candidates.inconsistent" :key="i">
                  <td class="text-caption">{{ comboSummary(c.combo) }}</td>
                  <td>stability {{ Number(c.stabilityScore).toFixed(2) }}</td>
                  <td>won {{ c.topFoldAppearances }}/{{ c.sufficientFoldCount }} folds' top bracket</td>
                </tr>
                <tr v-if="!selectedRun.ranked_candidates.inconsistent.length">
                  <td class="text-medium-emphasis text-center py-3">None.</td>
                </tr>
              </tbody>
            </v-table>

            <div class="text-subtitle-2 font-weight-medium mb-2">Insufficient data</div>
            <v-table density="compact" class="custom-data-table">
              <tbody>
                <tr v-for="(c, i) in selectedRun.ranked_candidates.insufficientData" :key="i">
                  <td class="text-caption">{{ comboSummary(c.combo) }}</td>
                  <td>{{ c.sufficientFoldCount }}/{{ c.totalFoldCount }} folds had enough trades</td>
                </tr>
                <tr v-if="!selectedRun.ranked_candidates.insufficientData.length">
                  <td class="text-medium-emphasis text-center py-3">None.</td>
                </tr>
              </tbody>
            </v-table>
          </template>
          <v-progress-linear v-else indeterminate class="mt-2" />
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import { fetchWrapper } from '@/helpers/fetch-wrapper';

function dateKey(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function todayDateKey() {
  return dateKey(new Date());
}

// Defaults the "From" field to 3 months back -- a reasonable starting range
// for a first optimization run, not a hard requirement.
function threeMonthsAgoDateKey() {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return dateKey(d);
}

export default {
  name: 'ParamOptimizer-page',
  data() {
    const today = todayDateKey();
    const fromDate = threeMonthsAgoDateKey();
    return {
      form: { fromDate, toDate: today, exitStrategyMode: 'single_stage', searchStrategy: 'grid', foldCount: null },
      searchStrategyOptions: [
        { title: 'Grid (exhaustive)', value: 'grid' },
        { title: 'Coordinate descent (faster)', value: 'coordinate' },
        { title: 'Wide random search', value: 'random' },
      ],
      starting: false,
      startMessage: '',
      startError: false,
      runs: [],
      runsLoaded: true,
      selectedRun: null,
      viewingRun: false,
      viewError: '',
      promoting: null,
      pollTimer: null,
    };
  },
  mounted() {
    this.loadRuns();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    isFiniteNumber(value) {
      return value !== null && value !== undefined && Number.isFinite(Number(value));
    },
    formatMoney(value) {
      return this.isFiniteNumber(value) ? this.$helper.formatPrice(Number(value)) : '—';
    },
    statusColor(status) {
      return { running: 'info', completed: 'success', failed: 'error' }[status] || 'default';
    },
    confidenceColor(tier) {
      if (tier === 'reliable') return 'success';
      if (tier === 'limited') return 'warning';
      return 'error';
    },
    comboSummary(combo) {
      return Object.entries(combo || {}).map(([k, v]) => `${k}: ${v}`).join(', ');
    },
    startRun() {
      if (!this.form.fromDate || !this.form.toDate) return;
      this.starting = true;
      this.startMessage = '';
      const payload = { ...this.form, foldCount: this.form.foldCount || undefined };
      fetchWrapper.post('api/param-optimizer/runs', payload).then((res) => {
        this.startMessage = `Run #${res?.data?.runId} started (~${res?.data?.estimatedEvaluations ?? '?'} evaluations estimated). Refresh Run History to track progress.`;
        this.startError = false;
        this.loadRuns();
        this.startPolling();
      }).catch((err) => {
        this.startMessage = typeof err === 'string' ? err : 'Failed to start optimization run.';
        this.startError = true;
      }).finally(() => {
        this.starting = false;
      });
    },
    loadRuns() {
      this.runsLoaded = false;
      return fetchWrapper.get('api/param-optimizer/runs').then((res) => {
        this.runs = res?.data?.runs || [];
        this.runsLoaded = true;
        if (this.runs.some((r) => r.status === 'running')) this.startPolling();
        else this.stopPolling();
      }).catch((err) => {
        console.error('Failed to load optimization runs:', err);
        this.runs = [];
        this.runsLoaded = true;
      });
    },
    startPolling() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => {
        this.loadRuns();
        if (this.selectedRun) this.viewRun(this.selectedRun.param_optimizer_run_id, { silent: true });
      }, 10000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    viewRun(runId, { silent = false } = {}) {
      if (!silent) this.viewingRun = true;
      return fetchWrapper.get(`api/param-optimizer/runs/${runId}`).then((res) => {
        this.selectedRun = res?.data || null;
        this.viewError = '';
      }).catch((err) => {
        console.error(`Failed to load run ${runId}:`, err);
        // A silent poll (see startPolling()) keeps whatever's already shown
        // rather than clobbering it with an error banner every 10s.
        if (!silent) this.viewError = typeof err === 'string' ? err : `Failed to load run #${runId}.`;
      }).finally(() => {
        if (!silent) this.viewingRun = false;
      });
    },
    promote(candidateIndex) {
      if (!this.selectedRun) return;
      this.promoting = candidateIndex;
      fetchWrapper.post(`api/param-optimizer/runs/${this.selectedRun.param_optimizer_run_id}/promote`, { candidateIndex }).then((res) => {
        this.startMessage = `Saved as settings version #${res?.data?.version}. Review it on the Settings page's Settings History card.`;
        this.startError = false;
      }).catch((err) => {
        this.startMessage = typeof err === 'string' ? err : 'Failed to save this candidate.';
        this.startError = true;
      }).finally(() => {
        this.promoting = null;
      });
    },
  },
};
</script>
